import { MessageHandler, WebSocketDataType, WebSocketMessage } from '@/types/websocket'

export class WebSocketService {
    readonly maxReconnectAttempts = 5
    readonly baseDelay = 1000
    readonly messageHandlers: Map<string, MessageHandler<any>> = new Map()
    readonly url: string
    private socket: WebSocket | null = null
    private reconnectAttempts = 0
    private onOpenCallbacks: (() => void)[] = []

    constructor(url: string) {
        this.url = url
    }

    getUrl(): string {
        return this.url
    }

    public connect(): void {
        try {
            this.socket = new WebSocket(this.url)
            this.setupEventHandlers()
        } catch (error) {
            console.error('WebSocket connection error:', error)
            this.reconnect()
        }
    }

    public onOpen(callback: () => void): void {
        if (this.isConnected()) {
            callback()
        } else {
            this.onOpenCallbacks.push(callback)
        }
    }

    public registerHandler<T extends WebSocketDataType>(type: string, handler: MessageHandler<T>): void {
        this.messageHandlers.set(type, handler)
    }

    public send(message: Partial<WebSocketMessage>): void {
        if (this.socket?.readyState === WebSocket.OPEN) {
            this.socket.send(JSON.stringify(message))
        } else {
            console.error('WebSocket is not connected')
        }
    }

    public disconnect(): void {
        if (this.socket) {
            this.socket.close()
            this.socket = null
        }
    }

    public isConnected(): boolean {
        return this.socket?.readyState === WebSocket.OPEN
    }

    private setupEventHandlers(): void {
        if (!this.socket) return

        this.socket.onopen = () => {
            console.log('Connected to WebSocket server')
            this.reconnectAttempts = 0
            this.onOpenCallbacks.forEach(callback => callback())
            this.onOpenCallbacks = []
        }

        this.socket.onmessage = (event: MessageEvent) => {
            try {
                const message = JSON.parse(event.data) as WebSocketMessage
                const handler = this.messageHandlers.get(message.type)
                if (handler) {
                    handler(message.data)
                } else {
                    console.log('Unknown message type:', message.type)
                }
            } catch (error) {
                console.error('Error processing message:', error)
            }
        }

        this.socket.onclose = () => {
            console.log('WebSocket connection closed')
            this.reconnect()
        }

        this.socket.onerror = (error: Event) => {
            console.error('WebSocket error:', error)
        }
    }

    private reconnect(): void {
        if (this.reconnectAttempts >= this.maxReconnectAttempts) {
            console.error('Max reconnection attempts reached')
            return
        }

        const delay = this.baseDelay * Math.pow(2, this.reconnectAttempts)
        this.reconnectAttempts++

        console.log(`Reconnecting in ${delay}ms... (Attempt ${this.reconnectAttempts})`)
        setTimeout(() => this.connect(), delay)
    }
}
